/**
 * Presupuestos, pedidos y albaranes.
 *
 * §§48-50 del prompt maestro. Aquí se calcula dinero que después va a una
 * factura encadenada con Veri*Factu (ADR-008), y eso impone una regla que
 * parece un detalle y no lo es:
 *
 * **El IVA se calcula sobre la base acumulada de cada tipo impositivo, no
 * sumando el IVA línea a línea.** Redondear en cada línea y sumar produce
 * diferencias de céntimos respecto al desglose que la AEAT espera. En un
 * presupuesto eso es una discusión con el cliente; en una factura
 * registrada y encadenada, es un registro que no cuadra y que **ya no se
 * puede corregir sin emitir una rectificativa**.
 *
 * Por eso el desglose por tipo se calcula aquí, en el dominio, una sola vez
 * y de la misma forma para presupuesto, albarán y factura.
 */

import { ConflictError, ValidationError } from './errors.js';
import { AMOUNT_SCALE, Money, UNIT_PRICE_SCALE } from './money.js';

// ═══════════════════════════════════════════════════════════════════════
// 1. Líneas
// ═══════════════════════════════════════════════════════════════════════

export const LINE_KINDS = [
  'PRODUCTO',
  'SERVICIO',
  'MANO_OBRA',
  'DESPLAZAMIENTO',
  /** Línea de texto sin importe: agrupa o explica. */
  'TEXTO',
] as const;
export type LineKind = (typeof LINE_KINDS)[number];

export interface SalesLineInput {
  readonly lineId: string;
  readonly kind: LineKind;
  readonly description: string;
  /** Cantidad como decimal exacto: '2', '1.5' horas, '12.75' m. */
  readonly quantity: string;
  readonly unitPrice: Money;
  /** Descuento de línea en porcentaje: '10' = 10 %. */
  readonly discountRate?: string | null;
  /** Tipo de IVA aplicable: '21', '10', '4', '0'. */
  readonly taxRate: string;
  /** Coste unitario para el margen. No sale nunca al cliente. */
  readonly unitCost?: Money | null;
}

export interface SalesLineTotals {
  readonly lineId: string;
  readonly gross: Money;
  readonly discount: Money;
  /** Base imponible de la línea. */
  readonly net: Money;
  readonly taxRate: string;
  readonly cost: Money | null;
}

/**
 * Totales de una línea.
 *
 * ⚠️ **No devuelve el IVA de la línea.** No es un olvido: el IVA no se
 * calcula por línea. Si lo devolviera, alguien lo sumaría, y ese es
 * exactamente el error que este módulo evita.
 */
export function computeLineTotals(line: SalesLineInput): SalesLineTotals {
  if (line.kind === 'TEXTO') {
    const zero = Money.zero(line.unitPrice.currency, AMOUNT_SCALE);
    return { lineId: line.lineId, gross: zero, discount: zero, net: zero, taxRate: line.taxRate, cost: null };
  }

  assertRate(line.taxRate, 'tipo de IVA');
  if (line.discountRate) assertRate(line.discountRate, 'descuento');

  // El precio unitario conserva su precisión (6 decimales); el importe de
  // la línea se redondea UNA vez, al pasar a importe.
  const gross = line.unitPrice
    .rescale(UNIT_PRICE_SCALE)
    .multiply(line.quantity, UNIT_PRICE_SCALE)
    .rescale(AMOUNT_SCALE);

  const discount = line.discountRate
    ? gross.percentage(line.discountRate, AMOUNT_SCALE)
    : Money.zero(gross.currency, AMOUNT_SCALE);

  const cost = line.unitCost
    ? line.unitCost.rescale(UNIT_PRICE_SCALE).multiply(line.quantity, UNIT_PRICE_SCALE).rescale(AMOUNT_SCALE)
    : null;

  return {
    lineId: line.lineId,
    gross,
    discount,
    net: gross.subtract(discount),
    taxRate: line.taxRate,
    cost,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 2. Totales del documento y desglose de IVA
// ═══════════════════════════════════════════════════════════════════════

export interface TaxBreakdownEntry {
  readonly taxRate: string;
  readonly taxableBase: Money;
  readonly taxAmount: Money;
}

export interface DocumentTotals {
  readonly lines: readonly SalesLineTotals[];
  readonly gross: Money;
  readonly lineDiscounts: Money;
  /** Descuento global repartido entre líneas, sin perder céntimos. */
  readonly globalDiscount: Money;
  readonly taxableBase: Money;
  /** Desglose por tipo: lo que exige la factura (y Veri*Factu). */
  readonly taxBreakdown: readonly TaxBreakdownEntry[];
  readonly taxTotal: Money;
  readonly total: Money;
  readonly cost: Money | null;
}

export interface DocumentTotalsOptions {
  /** Descuento global en porcentaje sobre la base tras descuentos de línea. */
  readonly globalDiscountRate?: string | null;
  readonly currency?: string;
}

/**
 * Totales del documento con desglose de IVA **por tipo impositivo**.
 *
 * El orden importa y es el que exige la normativa de facturación:
 *   1. importe bruto de cada línea
 *   2. descuentos de línea
 *   3. descuento global, repartido entre líneas por el método del mayor
 *      resto para que no aparezca ni desaparezca un céntimo
 *   4. **agrupación por tipo de IVA**
 *   5. IVA calculado UNA vez sobre cada base acumulada
 *
 * Invertir 4 y 5 —calcular el IVA por línea y sumarlo— da un resultado
 * distinto en cuanto hay más de dos o tres líneas, y es la causa clásica
 * de facturas que difieren en céntimos del desglose declarado.
 */
export function computeDocumentTotals(
  lines: readonly SalesLineInput[],
  options: DocumentTotalsOptions = {},
): DocumentTotals {
  const currency = options.currency ?? lines[0]?.unitPrice.currency ?? 'EUR';
  const zero = Money.zero(currency, AMOUNT_SCALE);

  const computed = lines.map(computeLineTotals);
  const billable = computed.filter((l) => !l.net.isZero() || l.gross.isZero() === false);

  const gross = Money.sum(computed.map((l) => l.gross), currency).rescale(AMOUNT_SCALE);
  const lineDiscounts = Money.sum(computed.map((l) => l.discount), currency).rescale(AMOUNT_SCALE);
  const afterLineDiscounts = gross.subtract(lineDiscounts);

  // ── Descuento global: se reparte, no se resta del total ────────────
  // Restarlo del total dejaría el desglose de IVA sin cuadrar con la base.
  let globalDiscount = zero;
  const netByLine = new Map(computed.map((l) => [l.lineId, l.net]));

  if (options.globalDiscountRate) {
    assertRate(options.globalDiscountRate, 'descuento global');
    globalDiscount = afterLineDiscounts.percentage(options.globalDiscountRate, AMOUNT_SCALE);

    if (!globalDiscount.isZero() && !afterLineDiscounts.isZero()) {
      const weights = billable.map((l) => Math.abs(Number(l.net.toString())));
      const totalWeight = weights.reduce((a, b) => a + b, 0);

      if (totalWeight > 0) {
        const shares = globalDiscount.allocate(weights);
        billable.forEach((line, index) => {
          netByLine.set(line.lineId, line.net.subtract(shares[index]!));
        });
      }
    }
  }

  // ── Agrupación por tipo impositivo ─────────────────────────────────
  const basesByRate = new Map<string, Money>();
  for (const line of computed) {
    const net = netByLine.get(line.lineId) ?? line.net;
    if (net.isZero()) continue;
    const current = basesByRate.get(line.taxRate) ?? zero;
    basesByRate.set(line.taxRate, current.add(net));
  }

  const taxBreakdown: TaxBreakdownEntry[] = [...basesByRate.entries()]
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([taxRate, taxableBase]) => ({
      taxRate,
      taxableBase,
      // Una sola operación de redondeo por tipo, sobre la base acumulada.
      taxAmount: taxableBase.percentage(taxRate, AMOUNT_SCALE),
    }));

  const taxableBase = Money.sum(taxBreakdown.map((t) => t.taxableBase), currency).rescale(AMOUNT_SCALE);
  const taxTotal = Money.sum(taxBreakdown.map((t) => t.taxAmount), currency).rescale(AMOUNT_SCALE);

  const costs = computed.map((l) => l.cost).filter((c): c is Money => c !== null);

  return {
    lines: computed.map((l) => ({ ...l, net: netByLine.get(l.lineId) ?? l.net })),
    gross,
    lineDiscounts,
    globalDiscount,
    taxableBase,
    taxBreakdown,
    taxTotal,
    total: taxableBase.add(taxTotal),
    cost: costs.length > 0 ? Money.sum(costs, currency).rescale(AMOUNT_SCALE) : null,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 3. Margen
// ═══════════════════════════════════════════════════════════════════════

export interface MarginSummary {
  readonly revenue: Money;
  readonly cost: Money;
  readonly margin: Money;
  /** Porcentaje sobre la venta, con dos decimales. Null si no hay venta. */
  readonly marginRate: string | null;
}

/**
 * Margen del documento.
 *
 * Se calcula sobre la **base imponible**, nunca sobre el total: el IVA no
 * es ingreso de la empresa, es dinero que se recauda para Hacienda. Un
 * margen calculado sobre el total con IVA se infla un 21 % y hace parecer
 * rentable un trabajo que no lo es.
 */
export function computeMargin(totals: DocumentTotals): MarginSummary | null {
  if (!totals.cost) return null;

  const revenue = totals.taxableBase;
  const margin = revenue.subtract(totals.cost);

  const revenueValue = Number(revenue.toString());
  const marginRate =
    revenueValue === 0 ? null : ((Number(margin.toString()) / revenueValue) * 100).toFixed(2);

  return { revenue, cost: totals.cost, margin, marginRate };
}

// ═══════════════════════════════════════════════════════════════════════
// 4. Presupuestos (§48)
// ═══════════════════════════════════════════════════════════════════════

export const QUOTE_STATUSES = [
  'BORRADOR',
  'ENVIADO',
  /** El cliente lo ha abierto. Dato comercial, no contractual. */
  'VISTO',
  'ACEPTADO',
  'RECHAZADO',
  'VENCIDO',
] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

const QUOTE_TRANSITIONS: Readonly<Record<QuoteStatus, readonly QuoteStatus[]>> = {
  BORRADOR: ['ENVIADO'],
  ENVIADO: ['VISTO', 'ACEPTADO', 'RECHAZADO', 'VENCIDO', 'BORRADOR'],
  VISTO: ['ACEPTADO', 'RECHAZADO', 'VENCIDO'],
  // 🔒 Un presupuesto aceptado es un acuerdo: no se retoca, se sustituye.
  ACEPTADO: [],
  // Un rechazo se puede revisar con una oferta nueva sobre el mismo hilo.
  RECHAZADO: ['BORRADOR'],
  VENCIDO: ['BORRADOR'],
};

export function canTransitionQuote(from: QuoteStatus, to: QuoteStatus): boolean {
  return QUOTE_TRANSITIONS[from].includes(to);
}

export function assertQuoteTransition(from: QuoteStatus, to: QuoteStatus): void {
  if (canTransitionQuote(from, to)) return;
  if (from === 'ACEPTADO') {
    throw new ConflictError(
      'Un presupuesto aceptado es un acuerdo con el cliente: para cambiarlo se emite uno nuevo que lo sustituya',
      { from, to },
    );
  }
  throw new ConflictError(`Transición de presupuesto no permitida: ${from} → ${to}`, { from, to });
}

/**
 * Un presupuesto sólo se edita mientras es borrador.
 *
 * Enviado ya está en manos del cliente: cambiar las líneas por debajo
 * significa que el cliente y la empresa están mirando documentos distintos
 * con el mismo número.
 */
export function assertQuoteEditable(status: QuoteStatus): void {
  if (status === 'BORRADOR') return;
  throw new ConflictError(
    `Un presupuesto en estado ${status} no se modifica: vuelva a borrador o emita uno nuevo`,
    { status },
  );
}

export interface QuoteValidity {
  readonly status: QuoteStatus;
  readonly validUntil: string | null;
}

/**
 * Estado efectivo del presupuesto en una fecha.
 *
 * El vencimiento es un hecho del calendario, no una acción de nadie: un
 * presupuesto con validez de 30 días vence aunque nadie toque el sistema.
 * Calcularlo al leer evita que la lista muestre como vigentes ofertas
 * caducadas hace meses porque el proceso nocturno falló una noche.
 */
export function effectiveQuoteStatus(quote: QuoteValidity, today: string): QuoteStatus {
  if (quote.status !== 'ENVIADO' && quote.status !== 'VISTO') return quote.status;
  if (!quote.validUntil) return quote.status;
  return quote.validUntil < today ? 'VENCIDO' : quote.status;
}

export function isQuoteOpen(status: QuoteStatus): boolean {
  return status === 'ENVIADO' || status === 'VISTO';
}

// ═══════════════════════════════════════════════════════════════════════
// 5. Albaranes (§50)
// ═══════════════════════════════════════════════════════════════════════

export const DELIVERY_NOTE_STATUSES = [
  'BORRADOR',
  /** Entregado y firmado por el cliente. */
  'ENTREGADO',
  'FACTURABLE',
  'FACTURADO',
  'ANULADO',
] as const;
export type DeliveryNoteStatus = (typeof DELIVERY_NOTE_STATUSES)[number];

const DELIVERY_TRANSITIONS: Readonly<Record<DeliveryNoteStatus, readonly DeliveryNoteStatus[]>> = {
  BORRADOR: ['ENTREGADO', 'ANULADO'],
  ENTREGADO: ['FACTURABLE', 'ANULADO'],
  FACTURABLE: ['FACTURADO', 'ANULADO'],
  // 🔒 Facturado: el albarán ya está dentro de un documento fiscal.
  FACTURADO: [],
  ANULADO: [],
};

export function assertDeliveryTransition(from: DeliveryNoteStatus, to: DeliveryNoteStatus): void {
  if (DELIVERY_TRANSITIONS[from].includes(to)) return;
  if (from === 'FACTURADO') {
    throw new ConflictError(
      'El albarán ya está facturado: la corrección se hace con una factura rectificativa',
      { from, to },
    );
  }
  throw new ConflictError(`Transición de albarán no permitida: ${from} → ${to}`, { from, to });
}

export interface PendingDeliveryNote {
  readonly deliveryNoteId: string;
  readonly number: string;
  readonly customerId: string;
  readonly customerName: string;
  readonly deliveredOn: string;
  readonly total: Money;
  readonly status: DeliveryNoteStatus;
  readonly signed: boolean;
}

export interface PendingToInvoice {
  readonly count: number;
  readonly total: Money;
  readonly oldestDays: number | null;
  /** Entregados pero sin firma: facturarlos invita a que no los paguen. */
  readonly unsigned: readonly PendingDeliveryNote[];
  readonly byCustomer: readonly {
    readonly customerId: string;
    readonly customerName: string;
    readonly count: number;
    readonly total: Money;
  }[];
}

/**
 * Albaranes entregados y sin facturar.
 *
 * Es el informe donde una empresa de mantenimiento descubre que lleva meses
 * trabajando gratis. El trabajo está hecho, el material puesto y el coste
 * pagado; lo único que falta es el paso que convierte todo eso en dinero.
 *
 * Se separan los **no firmados** porque facturar un albarán sin firma es
 * facturar algo que el cliente puede discutir: no es que no se deba hacer,
 * es que conviene saberlo antes y no cuando llega el impago.
 */
export function pendingToInvoice(
  notes: readonly PendingDeliveryNote[],
  today: string,
  currency = 'EUR',
): PendingToInvoice {
  const pending = notes.filter((n) => n.status === 'ENTREGADO' || n.status === 'FACTURABLE');

  const byCustomer = new Map<string, { customerId: string; customerName: string; count: number; total: Money }>();
  let oldestDays: number | null = null;

  for (const note of pending) {
    const age = daysBetween(note.deliveredOn, today);
    if (oldestDays === null || age > oldestDays) oldestDays = age;

    const current = byCustomer.get(note.customerId);
    if (current) {
      byCustomer.set(note.customerId, {
        ...current,
        count: current.count + 1,
        total: current.total.add(note.total),
      });
    } else {
      byCustomer.set(note.customerId, {
        customerId: note.customerId,
        customerName: note.customerName,
        count: 1,
        total: note.total,
      });
    }
  }

  return {
    count: pending.length,
    total: Money.sum(pending.map((n) => n.total), currency).rescale(AMOUNT_SCALE),
    oldestDays,
    unsigned: pending.filter((n) => !n.signed),
    byCustomer: [...byCustomer.values()].sort((a, b) => b.total.compare(a.total)),
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 6. Pedidos (§49)
// ═══════════════════════════════════════════════════════════════════════

export const SALES_ORDER_STATUSES = [
  'PENDIENTE',
  'EN_CURSO',
  'ENTREGADO',
  'CERRADO',
  'ANULADO',
] as const;
export type SalesOrderStatus = (typeof SALES_ORDER_STATUSES)[number];

const ORDER_TRANSITIONS: Readonly<Record<SalesOrderStatus, readonly SalesOrderStatus[]>> = {
  PENDIENTE: ['EN_CURSO', 'ANULADO'],
  EN_CURSO: ['ENTREGADO', 'ANULADO'],
  ENTREGADO: ['CERRADO'],
  CERRADO: [],
  ANULADO: [],
};

export function assertOrderTransition(from: SalesOrderStatus, to: SalesOrderStatus): void {
  if (ORDER_TRANSITIONS[from].includes(to)) return;
  throw new ConflictError(`Transición de pedido no permitida: ${from} → ${to}`, { from, to });
}

/**
 * Qué genera un presupuesto aceptado (§49).
 *
 * No se decide en la interfaz: depende de lo que contengan las líneas. Un
 * presupuesto de sólo mano de obra no necesita reservar material, y uno de
 * sólo suministro no necesita una orden de trabajo.
 */
export interface AcceptanceOutcome {
  readonly createsWorkOrder: boolean;
  readonly reservesStock: boolean;
  readonly requiresScheduling: boolean;
  readonly reasons: readonly string[];
}

export function planAcceptance(lines: readonly SalesLineInput[]): AcceptanceOutcome {
  const reasons: string[] = [];

  const hasProduct = lines.some((l) => l.kind === 'PRODUCTO');
  const hasWork = lines.some(
    (l) => l.kind === 'MANO_OBRA' || l.kind === 'SERVICIO' || l.kind === 'DESPLAZAMIENTO',
  );

  if (hasProduct) reasons.push('Hay líneas de producto: procede reservar material');
  if (hasWork) reasons.push('Hay líneas de trabajo: procede abrir una orden de trabajo');
  if (!hasProduct && !hasWork) reasons.push('El presupuesto no contiene líneas facturables');

  return {
    createsWorkOrder: hasWork || hasProduct,
    reservesStock: hasProduct,
    requiresScheduling: hasWork,
    reasons,
  };
}

// ═══════════════════════════════════════════════════════════════════════

function assertRate(value: string, label: string): void {
  if (!/^\d+(\.\d+)?$/.test(value)) {
    throw new ValidationError(`El ${label} debe ser un porcentaje positivo: "${value}"`);
  }
  if (Number(value) > 100) {
    throw new ValidationError(`El ${label} no puede superar el 100 %: "${value}"`);
  }
}

function daysBetween(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  return Math.max(0, Math.round(ms / 86_400_000));
}
